import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { LIVESTREAM_MODAL_CONFIG } from '@app-core/constants/constants';
import { TripDetailsPageQueryParams } from '@app-core/models/core.model';
import { CommonHttpService } from '@app-core/services/common-http/common-http.service';
import { DateService } from '@app-core/services/date/date.service';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { LiveTelematicsService } from '@app-core/services/live-telematics/live-telematics.service';
import { DEVICE_GPS_UPDATE_TIMER_MIN } from '@app-live-view/constants/live-view.constants';
import {
  IntitialActiveDevice,
  LiveDevice,
  LiveTelematicsMessage,
  LatestTripsAssetListResponse,
  LatestTripsAssetListRequest,
  AssetState,
} from '@app-live-view/models/live-view.model';
import { LivestreamModalComponent } from '@app-shared/components/livestream-modal/livestream-modal.component';
import { BehaviorSubject, EMPTY, Observable, of, timer } from 'rxjs';
import { WebRTCLivestreamModalComponent } from '@app-live-view/components/webrtc-livestream-modal/webrtc-livestream-modal.component';
import { DurationRangeService } from '@app-core/services/duration-range/duration-range.service';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';

// import { DataService } from '@app-core/services/data/data.service';

@Injectable()
export class LiveViewService {
  private allDevices: Map<string, LiveDevice> = new Map();

  constructor(
    private dialog: MatDialog,
    private commonHttpService: CommonHttpService,
    private router: Router,
    private dateService: DateService,
    private liveTelamaticsService: LiveTelematicsService,
    private durationRangeService: DurationRangeService,
    private gtmService: GoogleTagManagerService,
    private mixpanelService: MixPanelService
  ) {}

  public formatLatestTrips(trips = []): LiveDevice[] {
    const devices = trips
      .filter((trip) => {
        const { asset: { assetId = '' } = {} } = trip || {};
        const validTrip = !!assetId;
        return !!validTrip;
      })
      .map((trip) => {
        const initialActiveDevice = new IntitialActiveDevice(trip, this.dateService);
        this.allDevices.set(trip.asset.assetId, initialActiveDevice);
        return initialActiveDevice;
      });
    return devices;
  }

  public formatLiveTelematicsMessage(message: LiveTelematicsMessage): Observable<LiveDevice> {
    const { assetId, timestampUTC, eventType } = message.value;
    const ongoing = eventType !== 'Vehicle-Ignition-Off';
    const previousDeviceState = this.allDevices.get(assetId);
    if (!assetId || !previousDeviceState) {
      return EMPTY;
    }
    const state = this.liveTelamaticsService.getLiveDeviceState2({
      lastPingTimestampUTC: message.value.timestampUTC,
      ongoing,
    });
    const device = {
      ...previousDeviceState,
      ...message,
      ...message.value,
      timestamp: this.dateService.getLocalTimestamp(previousDeviceState.timezoneOffset, timestampUTC),
      ongoing,
      receivedLiveTelematicsUpdate: true,
      state: new BehaviorSubject(state),
    } as LiveDevice;
    if (device.stateTimerSubscription) {
      device.stateTimerSubscription.unsubscribe();
    }
    if (state === AssetState.Active) {
      device.stateTimerSubscription = timer(DEVICE_GPS_UPDATE_TIMER_MIN * 60 * 1000).subscribe(() => {
        device.state.next(AssetState.Inactive);
      });
    }
    return of(device);
  }

  public navigateToTripDetailsPage(device: LiveDevice, source: string) {
    const { tripId, deviceId, assetId, driverId, gpsData } = device;
    switch (device.state.getValue()) {
      case AssetState.Inactive:
        source === 'table'
          ? this.gtmService.gotoTripDetailsFromLiveViewTable('AMBER', assetId)
          : this.gtmService.gotoTripDetailsFromLiveViewMarker('AMBER', assetId);
        break;
      case AssetState.Active:
        source === 'table'
          ? this.gtmService.gotoTripDetailsFromLiveViewTable('ACTIVE', assetId)
          : this.gtmService.gotoTripDetailsFromLiveViewMarker('ACTIVE', assetId);
        break;

      default:
        source === 'table'
          ? this.gtmService.gotoTripDetailsFromLiveViewTable('INACTIVE', assetId)
          : this.gtmService.gotoTripDetailsFromLiveViewMarker('INACTIVE', assetId);
        break;
    }
    const queryParams = new TripDetailsPageQueryParams({
      tripId,
      deviceId,
      driverId,
      latitude: gpsData.latitude,
      longitude: gpsData.longitude,
    });
    this.router.navigate(['/trip-details'], { queryParams });
  }

  public openLivestreamModal(device: LiveDevice) {
    const { deviceId, assetId, deviceModel, tripId, asset, recordedInfo, isV2LiveStreamEnabled } = device;
    const { assetName } = device.asset;
    this.mixpanelService.track('Click on live streaming modal', {
      'Asset Id': assetId,
      'Asset Name': assetName,
    });
    let liveStreamModalData = {
      deviceId,
      deviceModel,
      assetId,
      assetName,
      tripId,
      asset,
      recordedInfo,
    };
    // todo - remove webRTC streaming flag from duration range to live telematics service
    if (this.durationRangeService.webRTCLiveStreamingEnabled && isV2LiveStreamEnabled) {
      this.dialog.open(WebRTCLivestreamModalComponent, {
        ...LIVESTREAM_MODAL_CONFIG,
        data: liveStreamModalData,
      });
    } else {
      this.dialog.open(LivestreamModalComponent, {
        ...LIVESTREAM_MODAL_CONFIG,
        data: liveStreamModalData,
      });
    }
  }

  public getLatestTripsAssetList(params: LatestTripsAssetListRequest): Observable<LatestTripsAssetListResponse> {
    return this.commonHttpService.getLatestTripsAssetList(params);
  }

  public setDevicePreviousStatus(assetId: string, device: LiveDevice) {
    this.allDevices.set(assetId, device);
  }
}
